/**
 * Block 14 «Раскрой» — local-first склад остатков (PR #13, CUT-3.4/3.5).
 *
 * Инвентарь остатков, общий между проектами пользователя (cross-project), в
 * localStorage по каноническому ключу `pomoshnik_offcuts_v1` (storageKeys §0.4).
 * Склад — UI-инвентарь устройства, НЕ ПДн/каталог (P-007 допускает localStorage);
 * серверная персистенция (контракт `WarehouseItem` с `userId`/uuid/`createdAt`) —
 * будущий B-234 + ADR (решение владельца STOP-pre PR #13).
 *
 * Форма записи — `WarehouseOffcut` (local-first, с «Имя/Партия»), зеркало
 * `ProjectOffcut` (см. schemas.ts). НЕ путать с замороженным `WarehouseItem`.
 *
 * Паттерн порта — `lib/processingDict.ts`: терпимость к битым/чужим данным,
 * молчаливая запись (приватный режим / квота не должны крэшить UI).
 */

import { genUuid } from '@/lib/uuid';
import { CUTTING_STORAGE_KEYS } from '../state/storageKeys';
import type { WarehouseOffcut } from '../model/types';

const KEY = CUTTING_STORAGE_KEYS.offcuts; // 'pomoshnik_offcuts_v1'

/** Валидна ли разобранная запись склада (терпим к чужим/битым данным). */
function isOffcut(v: unknown): v is WarehouseOffcut {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.length === 'number' &&
    typeof o.width === 'number' &&
    typeof o.qty === 'number'
  );
}

/** Нормализовать разобранную запись к канонической форме (строки → '', qty ≥ 1). */
function normalize(o: WarehouseOffcut): WarehouseOffcut {
  return {
    id: o.id,
    length: Math.max(0, o.length),
    width: Math.max(0, o.width),
    qty: Math.max(1, Math.floor(o.qty)),
    name: typeof o.name === 'string' ? o.name : '',
    material: typeof o.material === 'string' ? o.material : '',
    ...(typeof o.createdAt === 'string' ? { createdAt: o.createdAt } : {}),
    ...(typeof o.sourceProjectId === 'string' ? { sourceProjectId: o.sourceProjectId } : {}),
  };
}

/**
 * Загрузить весь склад из localStorage. Любая ошибка (нет localStorage / битый
 * JSON / не массив) → пустой склад `[]` (не крэш). Битые элементы отфильтровываются.
 */
export function getAll(): WarehouseOffcut[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isOffcut).map(normalize);
  } catch {
    return [];
  }
}

/** Сохранить весь склад в localStorage (молча игнорируем ошибки записи). */
function saveAll(items: WarehouseOffcut[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* localStorage недоступен (приватный режим / квота) — склад не критичен для работы */
  }
}

/**
 * Добавить записи в склад (накопление — push, как прототип `saveOffcuts`). Каждой
 * записи проставляется свежий `id` (genUuid), если не задан. Склад не дедуплицирует
 * — пользователь сам чистит инвентарь (× Удалить). Пустые (без размеров) записи
 * пропускаются. `createdAt` берётся как есть (caller проставляет — детерминизм
 * тестов, без `Date.now()` внутри стора). Возвращает обновлённый склад.
 */
export function add(items: WarehouseOffcut[]): WarehouseOffcut[] {
  const all = getAll();
  for (const item of items) {
    if (!(item.length > 0) || !(item.width > 0)) continue; // неполные строки-«леса» не складируем
    all.push(
      normalize({
        ...item,
        id: item.id || genUuid(),
      }),
    );
  }
  saveAll(all);
  return all;
}

/** Удалить запись склада по id. Возвращает обновлённый склад. */
export function remove(id: string): WarehouseOffcut[] {
  const all = getAll().filter((o) => o.id !== id);
  saveAll(all);
  return all;
}

/**
 * Заменить весь склад (replace-all) — для reactive-зеркала в сторе (PR #24): стор
 * владеет массивом склада и персистит его целиком при каждой правке (включая
 * строки-«леса» 0×0, которые юзер ещё редактирует; на раскрой они отфильтруются
 * `isCompleteOffcut`). Нормализует каждую запись. Молча игнорирует ошибки записи.
 */
export function save(items: WarehouseOffcut[]): void {
  saveAll(items.map(normalize));
}

/** Очистить весь склад. */
export function clear(): void {
  saveAll([]);
}

export const WarehouseStore = { getAll, add, remove, save, clear };
